/** The notification-rule catalog: template codes, labels, add-menu grouping and
 *  per-template argument metadata.
 *
 * The template/op code sets mirror `Notification_Rule.TEMPLATES` / `.OPS` on
 * the backend, anchored by `x-rule-templates` / `x-rule-ops` on
 * `ConsoleNotificationRuleRow` in console.openapi.yaml — a contract test
 * asserts the key sets match exactly, so a backend catalog change fails CI
 * here instead of rendering "Unknown template" at runtime. Labels are
 * display-only (dashboard-flavored where friendlier) and may differ from the
 * backend's verbose names.
 *
 * Argument WIRE SHAPES are dashboard-compatible by construction — every enum
 * list below stores exactly what `dashboard/src/notifications/NotificationRule.js`
 * writes (string enum codes, `{id: "<string>", name}` delivery methods,
 * `{value, label}` events, …). Do not "clean up" the types: the dashboard and
 * the backend matchers read these exact shapes.
 */

// ---------------------------------------------------------------------------
// Template codes (`Notification_Rule.TPL_*`)
// ---------------------------------------------------------------------------

export const TPL = {
  IS_APP_INSTALLED: 0,
  IS_TOUR_STAFF: 4,
  IS_IOS_USER: 5,
  IS_ANDROID_USER: 6,
  USER_LOGGED_IN: 7,
  USER_COUNTRY: 8,
  USER_LANG: 9,
  USER_OTA: 10,
  IS_LOCATION_SHARING_ENABLED: 11,
  IS_DIRECT_MESSAGE_OR_ANNOUNCEMENT: 12,
  IS_GROUP_CHAT: 13,
  IS_INTERNAL_STAFF_GUIDES_CHAT: 14,
  IS_STAFF_CHAT_GROUP: 75,
  ARE_PUSH_NOTIFICATIONS_ENABLED: 15,
  IS_RECEIVED_MESSAGE_AUTOMATED: 16,
  IS_CHECKED_IN: 17,
  IS_NO_SHOW: 18,
  IS_PASSENGER_COUNT_MODIFIED: 19,
  IS_RECEIVED_MESSAGE_TO_MAIN_OFFICE: 20,
  IS_UPDATED_VIA_INTEGRATION: 26,
  IS_UPDATED_VIA_XML: 27,
  IS_UPDATED_VIA_DASHBOARD: 28,
  IS_UPDATED_VIA_SELFREG_PAGE: 29,
  IS_UPDATED_VIA_TRIP_CODE: 30,
  IS_FIELD_AFFECTED: 31,
  IS_UPDATED_VIA: 32,
  USER_HAS_TAG_CATEGORY: 33,
  USER_HAS_TAG: 34,
  SURVEY_STATE: 35,
  NO_CONTACT_INFO: 36,
  IS_REBOOKING: 37,
  IS_FIELD_PRESENT: 38,
  TRIP_BEGINS_WITHIN_X_TIME: 39,
  REVIEW_IS_POSITIVE_NEGATIVE: 40,
  USER_IS_NEAR_LOCATION: 41,
  BOOKING_DATETIME_RANGE: 42,
  PHONE_COUNTRY: 43,
  REVIEW_RATING: 44,
  HAS_OTHER_TOURS: 45,
  IS_DIRECT_MESSAGE: 46,
  IS_ANNOUNCEMENT: 47,
  RULESET: 48,
  EVENT_TYPE: 49,
  TOUR_HAS_TAG_CATEGORY: 50,
  TOUR_HAS_TAG: 51,
  OTA_HAS_TAG_CATEGORY: 52,
  OTA_HAS_TAG: 53,
  TOUR_OPERATOR_HAS_TAG_CATEGORY: 54,
  TOUR_OPERATOR_HAS_TAG: 55,
  DELIVERY_METHOD: 56,
  IS_MAIN_BOOKING: 57,
  CHECKIN_SOURCE: 58,
  TRIP_TIME_RANGE: 59,
  PRECHECKIN_STATUS: 60,
  EMAIL_RECEIPT: 61,
  SETTLEMENT_METHOD: 62,
  ORDER_HAS_BOOKINGS: 63,
  MARKETING_CONSENT_GRANTED: 64,
  PRIVACY_POLICY_ACCEPTED: 65,
  HAS_SUB_BOOKINGS: 66,
  DATETIME_WITHIN_RANGE: 67,
  DATE_WITHIN_RANGE: 68,
  TIME_WITHIN_RANGE: 69,
  HAS_REAL_GUEST_NAMES: 70,
  // CP-8095. Note USER_HAS_TAG* (33/34) is a historical misnomer: those are the
  // BOOKING-level conditions. ACCOUNT_* below are the User-account ones.
  ORDER_HAS_TAG_CATEGORY: 71,
  ORDER_HAS_TAG: 72,
  ACCOUNT_HAS_TAG_CATEGORY: 73,
  ACCOUNT_HAS_TAG: 74,
} as const;

/** Expression-rule operators (`Notification_Rule.OPS`) with editor semantics. */
export const EXPR_OPS: { value: number; label: string; needsValue: boolean }[] = [
  { value: 0, label: 'equals (case-insensitive)', needsValue: true },
  { value: 1, label: '> (numeric)', needsValue: true },
  { value: 2, label: '≥ (numeric)', needsValue: true },
  { value: 3, label: '< (numeric)', needsValue: true },
  { value: 4, label: '≤ (numeric)', needsValue: true },
  { value: 5, label: 'contains (case-sensitive)', needsValue: true },
  { value: 6, label: 'is true (renders non-empty)', needsValue: false },
  { value: 7, label: 'is false (renders empty)', needsValue: false },
];

export const OP_IS_TRUE = 6;
export const OP_IS_FALSE = 7;

export function exprOpNeedsValue(op: number): boolean {
  return EXPR_OPS.find((o) => o.value === op)?.needsValue ?? true;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Display labels for the selectable templates (dashboard wording). */
export const RULE_TEMPLATE_LABELS: Record<number, string> = {
  0: 'User has app installed',
  4: 'Tour participant has a staff role',
  5: 'User has iOS app',
  6: 'User has Android app',
  7: 'User logged in',
  8: 'User country',
  9: 'User language',
  10: 'User registered via OTA',
  11: 'User has location sharing enabled',
  12: 'Message is to a personal conversation or is an announcement',
  13: 'Message is to group chat',
  14: 'Message is to INTERNAL (guides/staff) chat',
  // CP-8136: true for every thread on a guide-group channel -- announcements, the
  // group conversation and the per-guide personal threads alike.
  75: 'Message is in a guide group',
  15: 'User has push notifications enabled',
  16: 'Received message is automated',
  17: 'Tour participant checked in',
  18: "Tour participant didn't show up",
  19: 'Passenger count was modified',
  20: 'Received message is to Main Office/New Bookings',
  26: 'Tour participant was updated by an integration',
  27: 'Tour participant was updated by XML import',
  28: 'Tour participant was updated in the dashboard',
  29: 'Tour participant was updated at the self-registration page',
  30: 'Tour participant was updated via the trip code',
  31: 'Fields were affected by tour participant update',
  32: 'Tour participant updated via one of the methods',
  33: 'Tour participant has one of the tag categories',
  34: 'Tour participant has one of the tags',
  35: 'Tour participant survey state',
  36: 'User has no direct contact info',
  37: 'Booking was rebooked',
  38: 'Field exists and has value',
  39: 'Tour begins within X hours',
  40: 'Review is positive/negative',
  41: 'User enters location radius',
  42: 'Booking time is within X hours',
  43: "User's phone country",
  44: 'Review has rating',
  45: 'User has other tours',
  46: 'Message is to a personal conversation',
  47: 'Message is an announcement',
  48: 'Include ruleset',
  49: 'Event type',
  50: 'Tour has one of the tag categories',
  51: 'Tour has one of the tags',
  52: 'OTA has one of the tag categories',
  53: 'OTA has one of the tags',
  54: 'Tour operator has one of the tag categories',
  55: 'Tour operator has one of the tags',
  56: 'Delivery method',
  57: 'Tour participant is the main booker',
  58: 'Check-in source',
  59: 'Trip time range',
  60: 'Pre-check-in status',
  61: 'Email receipt',
  62: 'Settlement method',
  63: 'Order has bookings',
  64: 'Marketing consent granted',
  65: 'Privacy policy accepted',
  66: 'Booking has subbookings',
  67: 'Datetime within range',
  68: 'Date within range',
  69: 'Time within range',
  70: 'Booking has real guest names',
  71: 'Order has one of the tag categories',
  72: 'Order has one of the tags',
  73: 'User account has one of the tag categories',
  74: 'User account has one of the tags',
};

/** Retired templates: never offered in the add menu, rendered read-only with
 *  their historical label when present in old data. */
export const DEPRECATED_TEMPLATE_LABELS: Record<number, string> = {
  1: 'Tour participant used self-registration link',
  2: 'Tour participant used trip code',
  3: 'Tour participant added via dashboard',
  21: 'Tour participant created by integration',
  22: 'Tour participant imported from XML',
  23: 'Tour participant added via dashboard',
  24: 'Tour participant registered via trip code',
  25: 'Tour participant added from a tag',
};

export const DEPRECATED_TPLS: ReadonlySet<number> = new Set(
  Object.keys(DEPRECATED_TEMPLATE_LABELS).map(Number),
);

export function ruleTemplateLabel(tpl: number | null): string {
  if (tpl === null) return 'No template selected';
  return (
    RULE_TEMPLATE_LABELS[tpl] ?? DEPRECATED_TEMPLATE_LABELS[tpl] ?? `Unknown template #${tpl}`
  );
}

// ---------------------------------------------------------------------------
// Argument metadata
// ---------------------------------------------------------------------------

/** Selectable templates that take no arguments (`args` stays null). */
export const NO_ARGS_TPLS: ReadonlySet<number> = new Set([
  0, 4, 5, 6, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  26, 27, 28, 29, 30, 36, 46, 47, 57, 63, 64, 65, 70, 75,
]);

/** Default args seeded when a template is selected (and on tpl switch — the
 *  matchers only read their own keys, so a clean object beats stale leftovers;
 *  notably a stale `delivery_methods` without `allow_undefined` would crash
 *  the tpl-56 matcher). Shapes mirror what the dashboard writes. */
export function defaultArgs(tpl: number): Record<string, unknown> | null {
  switch (tpl) {
    case TPL.USER_LOGGED_IN:
      return null; // {period} added on input; absent = "logged in at all"
    case TPL.USER_COUNTRY:
    case TPL.PHONE_COUNTRY:
      return { country: [] };
    case TPL.USER_LANG:
      return { lang: null };
    case TPL.USER_OTA:
      return { ota: [], unique_emails: 0, direct: 0, app_promotion: 0 };
    case TPL.IS_FIELD_AFFECTED:
      return { affected_fields: [] };
    case TPL.IS_UPDATED_VIA:
      return { update_sources: [] };
    case TPL.USER_HAS_TAG_CATEGORY:
    case TPL.TOUR_HAS_TAG_CATEGORY:
    case TPL.OTA_HAS_TAG_CATEGORY:
    case TPL.TOUR_OPERATOR_HAS_TAG_CATEGORY:
    case TPL.ORDER_HAS_TAG_CATEGORY:
    case TPL.ACCOUNT_HAS_TAG_CATEGORY:
      return { tag_categories: [] };
    case TPL.USER_HAS_TAG:
    case TPL.TOUR_HAS_TAG:
    case TPL.OTA_HAS_TAG:
    case TPL.TOUR_OPERATOR_HAS_TAG:
    case TPL.ORDER_HAS_TAG:
    case TPL.ACCOUNT_HAS_TAG:
      return { tags: [] };
    case TPL.SURVEY_STATE:
      return { survey_state: [] };
    case TPL.IS_REBOOKING:
      return { rebooking: { from: false, to: false } };
    case TPL.IS_FIELD_PRESENT:
      return { match_all: true, fields: [] };
    case TPL.TRIP_BEGINS_WITHIN_X_TIME:
    case TPL.BOOKING_DATETIME_RANGE:
      return { raw_delta: '0', unit: 3600, delta: 0 };
    case TPL.REVIEW_IS_POSITIVE_NEGATIVE:
      return { no_review: false, positive_review: false, negative_review: false };
    case TPL.USER_IS_NEAR_LOCATION:
      return { latitude: '', longitude: '', radius: '', fetch_method: 'gps' };
    case TPL.REVIEW_RATING:
      return { low: 0, high: 5 };
    case TPL.HAS_OTHER_TOURS:
      return {
        same_departure: false,
        same_tour: false,
        time: { future: false, active: false, past: false },
        // CP-8264: per-flag reference moment ("now" = legacy fire-time behavior)
        time_ref: { future: 'now', active: 'now', past: 'now' },
        tours: [],
        // CP-8264: tours matched by tag — UNIONed with the explicit tour list
        tour_tags: { match_mode: 'and', tags: [] },
      };
    case TPL.EVENT_TYPE:
      return { events: [] };
    case TPL.DELIVERY_METHOD:
      return { delivery_methods: [], allow_undefined: false };
    case TPL.CHECKIN_SOURCE:
      return { sources: [] };
    case TPL.TRIP_TIME_RANGE:
      return {
        future: false, future_delta: 0,
        present: false, low_delta: 0, high_delta: 0,
        past: false, past_delta: 0,
      };
    case TPL.PRECHECKIN_STATUS:
      return { precheckin_statuses: [] };
    case TPL.EMAIL_RECEIPT:
      return { email_receipt: false };
    case TPL.SETTLEMENT_METHOD:
      return { settlement_method: [] };
    case TPL.HAS_SUB_BOOKINGS:
      return { scope: 'any' };
    case TPL.DATETIME_WITHIN_RANGE:
    case TPL.DATE_WITHIN_RANGE:
    case TPL.TIME_WITHIN_RANGE:
      return { input: '', comparison: 'any_between', from: null, to: null, tz: 'utc' };
    default:
      return null; // no-arg templates + tpl 48 (target lives on the rule)
  }
}

// ---------------------------------------------------------------------------
// Add-menu grouping
// ---------------------------------------------------------------------------

export interface AddMenuGroup {
  label: string;
  tpls: number[];
}

export const ADD_MENU_GROUPS: AddMenuGroup[] = [
  { label: 'User', tpls: [0, 5, 6, 7, 8, 43, 9, 11, 15, 36, 10, 64, 65] },
  {
    label: 'Booking & participant',
    tpls: [4, 57, 17, 18, 58, 60, 19, 31, 32, 26, 27, 28, 29, 30, 35, 37, 38, 61, 62, 63, 66, 70],
  },
  { label: 'Tour & schedule', tpls: [39, 42, 59, 45, 67, 68, 69] },
  { label: 'Messages', tpls: [46, 47, 12, 13, 14, 75, 16, 20] },
  { label: 'Reviews', tpls: [40, 44] },
  // Grouped by tag SOURCE, in the TAG_SOURCES order (booking, order, user,
  // tour, ota, tenant) so the menu reads as one family. 33/34 are the booking
  // ones despite their historical "Tour participant …" labels.
  { label: 'Tags', tpls: [33, 34, 71, 72, 73, 74, 50, 51, 52, 53, 54, 55] },
  // 48 is also reachable via the dedicated "Include ruleset" button; it stays
  // in the menu so type-ahead ("ruleset") finds it too.
  { label: 'Delivery & meta', tpls: [56, 49, 48, 41] },
];

// ---------------------------------------------------------------------------
// Enum option lists (dashboard-shape values — see module docstring)
// ---------------------------------------------------------------------------

/** `update_sources` (tpl 32) — stored as STRING codes, dashboard-style. */
export const PARTICIPANT_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Dashboard' },
  { value: '1', label: 'Self-registration' },
  { value: '2', label: 'Trip code' },
  { value: '3', label: 'XML' },
  { value: '4', label: 'Integration' },
  { value: '5', label: 'Other' },
  { value: '6', label: 'Tag' },
  { value: '7', label: 'Booking reference' },
  { value: '9', label: 'Catalog' },
  { value: '10', label: 'Parent booking' },
  { value: '11', label: 'Console' },
];

/** `survey_state` (tpl 35) — string codes. */
export const SURVEY_STATE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Not sent' },
  { value: '1', label: 'Sent' },
  { value: '2', label: 'Opened' },
  { value: '3', label: 'Submitted' },
  { value: '4', label: 'Not needed' },
  { value: '5', label: 'Emitted, but not sent yet' },
];

/** `sources` (tpl 58) — string codes. */
export const CHECKIN_SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'App' },
  { value: '1', label: 'Booking system' },
  { value: '2', label: 'Dashboard' },
  { value: '3', label: 'Test' },
  { value: '4', label: 'Console' },
];

/** `precheckin_statuses` (tpl 60) — string codes, listed in progress order
 *  (0 → 1 → 3 → 2): 3 = every guest has saved details or is skipped, but the final
 *  waivers/consent screen was not submitted. */
export const PRECHECKIN_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Not completed' },
  { value: '1', label: 'Partially completed' },
  { value: '3', label: 'Details completed' },
  { value: '2', label: 'Completed' },
];

/** `settlement_method` (tpl 62) — string codes. */
export const SETTLEMENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'DIRECT', label: 'Direct' },
  { value: 'VOUCHER', label: 'Voucher' },
  { value: 'DEPOSIT', label: 'Deposit' },
  { value: 'WHOLESALE', label: 'Wholesale' },
  { value: 'DEFERRED', label: 'Deferred' },
];

/** `delivery_methods` (tpl 56) — the dashboard stores these exact
 *  `{id: "<string>", name}` objects (genOptions shape, STRING ids). */
export const RULE_DELIVERY_METHOD_OPTIONS: { id: string; name: string }[] = [
  { id: '0', name: 'Email' },
  { id: '1', name: 'SMS' },
  { id: '2', name: 'Chat' },
  { id: '6', name: 'HTTP Request' },
  { id: '7', name: 'WhatsApp' },
  { id: '8', name: 'Airbnb' },
];

/** `comparison` (tpls 67/68/69) — dashboard DT_RANGE_COMPARISONS. */
export const DT_RANGE_COMPARISONS: { value: string; label: string }[] = [
  { value: 'any_between', label: 'any value is between' },
  { value: 'all_between', label: 'all values are between' },
  { value: 'any_gt', label: 'any value is greater than' },
  { value: 'all_gt', label: 'all values are greater than' },
  { value: 'any_gte', label: 'any value is greater than or equal to' },
  { value: 'all_gte', label: 'all values are greater than or equal to' },
  { value: 'any_lt', label: 'any value is less than' },
  { value: 'all_lt', label: 'all values are less than' },
  { value: 'any_lte', label: 'any value is less than or equal to' },
  { value: 'all_lte', label: 'all values are less than or equal to' },
];

/** Per-field comparison ops for "Field exists" rows (tpl 38). */
export const FIELD_EXISTS_OPS: { value: string; label: string }[] = [
  { value: 'icontains', label: 'contains (case-insensitive)' },
  { value: 'contains', label: 'contains' },
  { value: 'exact', label: 'equals' },
  { value: 'iexact', label: 'equals (case-insensitive)' },
  { value: 'in', label: 'is contained in' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
];

export const FIELD_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'path', label: 'Path (dotty lookup)' },
  { value: 'template', label: 'Template (DTL)' },
  { value: 'constant', label: 'Constant' },
];

export const VALUE_MODE_OPTIONS = [
  { value: 'constant', label: 'Constant' },
  { value: 'path', label: 'Path (dotty lookup)' },
  { value: 'template', label: 'Template (DTL)' },
];

/** Delta units for "begins within X" rules (39/42). */
export const TIME_UNIT_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'seconds' },
  { value: 60, label: 'minutes' },
  { value: 3600, label: 'hours' },
  { value: 86400, label: 'days' },
];

/** Tri-state selects on the OTA rule (tpl 10). */
export const OTA_TRISTATE_OPTIONS: Record<
  'unique_emails' | 'direct' | 'app_promotion',
  { value: number; label: string }[]
> = {
  unique_emails: [
    { value: 0, label: 'Do not check' },
    { value: 1, label: 'Unique emails only' },
    { value: 2, label: 'Non-unique emails only' },
  ],
  direct: [
    { value: 0, label: 'Do not check' },
    { value: 1, label: 'Direct/internal only' },
    { value: 2, label: 'Not direct/internal' },
  ],
  app_promotion: [
    { value: 0, label: 'Do not check' },
    { value: 1, label: 'App promotion allowed' },
    { value: 2, label: 'App promotion prohibited' },
  ],
};
